import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { sentry } from '../../../../sentry';
import { ArchiveLocationSectorInput, UnarchiveLocationSectorInput } from '../../api/location-sectors/location-sectors.type';
import { CustomMessagesService } from '../../errors/custom-messages.service';
import { delay } from '../../utils/delay';
import { ArchiveLocationEnvironmentApi } from './archive-location-environment.api';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface ArchiveEnvironmentDialogData {
  sectorId: string;
  archived: boolean;
  name: string;
}

export interface ArchiveEnvironmentDialogResult {
  reload: boolean;
}

type ArchiveEnvironmentDialogSelection = 'ARCHIVE_ENVIRONMENTS' | 'UNARCHIVE_ENVIRONMENTS' | 'ARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS' | 'UNARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS';

@Component({
  templateUrl: './archive-environment-dialog.component.html',
  styleUrls: ['./archive-environment-dialog.component.scss']
})
export class ArchiveEnvironmentDialogComponent {

  public selected: ArchiveEnvironmentDialogSelection = null;

  public isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: ArchiveEnvironmentDialogData,
    private readonly matDialogRef: MatDialogRef<ArchiveEnvironmentDialogComponent, ArchiveEnvironmentDialogResult>,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly archiveLocationSectorApi: ArchiveLocationEnvironmentApi,
    private readonly messageService: CustomMessagesService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async updateArchived(): Promise<void> {
    try {
      this.isLoading = true;
      if (this.data.archived) {
        await Promise.all([
          this.archiveLocationSectorApi.unarchive(this.prepareUnarchivePayload()),
          delay(1000)
        ]);
        this.messageService.success('ARCHIVE_ENVIRONMENT_DIALOG.UNARCHIVE.SUCCESS');
      } else {
        await Promise.all([
          await this.archiveLocationSectorApi.archive(this.prepareArchivePayload()),
          delay(1000)
        ]);
        this.messageService.success('ARCHIVE_ENVIRONMENT_DIALOG.ARCHIVE.SUCCESS');
      }
    } catch (err) {
      sentry.captureException(err);
      this.messageService.error('UNKNOWN_ERROR');
    } finally {
      this.matDialogRef.close({ reload: true });
    }

  }

  prepareArchivePayload(): ArchiveLocationSectorInput {
    return {
      id: this.data.sectorId,
      archiveEquipments: this.selected === 'ARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS',
    };
  }

  prepareUnarchivePayload(): UnarchiveLocationSectorInput {
    return {
      id: this.data.sectorId,
      unarchiveEquipments: this.selected === 'UNARCHIVE_ENVIRONMENTS_AND_EQUIPMENTS',
    };
  }
}
